// The growth timeline's two encodings, and the rule keeping them apart.
//
// MASTERY is hue, and it is the matrix's hue: green outright, blue with ABii's
// help, amber not yet, imported from `matrix/states` rather than restated, so a
// learner's row in the grid and their timeline cannot come to disagree about
// what a colour means.
//
// GRADE-RELATIVE placement is TEXTURE, riding on top: a hatch that rises to the
// right for work above the learner's grade, one that falls for work below it,
// solid at grade, dots for content whose level we don't know. The slope agrees
// with the word, which is the only reason a reader has to remember which is
// which.
// A second hue was the obvious move and it is the wrong one — the palette is
// already full (see tokens.LEVEL_REL, which reached the same conclusion for the
// district dashboard's chips and spends a glyph and a word instead), and a
// chart carrying two categorical hues at once makes neither readable. Texture
// also survives the two cases hue doesn't: colourblind viewers and print.
//
// The wording comes from LEVEL_REL for the same reason the colours come from
// `states`: "above grade level" should be one string in this app. Only the keys
// differ — the growth endpoint says `at` where the district dashboard says `on`
// — so REL maps between them and nothing else does.
import React from "react";
import { PROSE, LEVEL_REL } from "../../impact/tokens";
import { STANDARD_STATES } from "../matrix/states";

// Mastery states, in the order every composition in this app stacks them:
// most independent first.
const STATES = ["outright", "assisted", "not_yet"];
const stateOf = (key) => STANDARD_STATES[key];

// Grade-relative placement, in number-line order with the unknowns last.
// `pattern` names the SVG pattern in <GrowthDefs>; `swatch` is the CSS
// equivalent for a Legend swatch, which has no access to the SVG's defs.
// The two hatches are named for the direction they RISE in, not for an angle,
// and they are assigned so the mark agrees with the word: work above grade level
// hatches upward, work below it hatches downward. That is the whole mnemonic,
// and it is the reason these are `up`/`down` rather than `hatch45`/`hatch135` —
// an angle in the name is a number two different rendering models disagree
// about (see SWATCH_CSS), and the first thing that went wrong here was exactly
// that disagreement, with the legend showing one slope and the plot the other.
const REL = {
  below: {
    key: "below",
    token: "below",
    pattern: "down",
    label: LEVEL_REL.below.long,
    short: LEVEL_REL.below.label,
  },
  at: {
    key: "at",
    token: "on",
    pattern: "solid",
    label: LEVEL_REL.on.long,
    short: LEVEL_REL.on.label,
  },
  above: {
    key: "above",
    token: "above",
    pattern: "up",
    label: LEVEL_REL.above.long,
    short: LEVEL_REL.above.label,
  },
  unknown: {
    key: "unknown",
    token: "unknown",
    pattern: "dots",
    label: LEVEL_REL.unknown.long,
    short: LEVEL_REL.unknown.label,
  },
};

// Stacking order inside a state: below grade sits against the axis, so the
// hardest-to-read texture is the one nearest a straight edge, and the four read
// outward as a number line.
const REL_ORDER = ["below", "at", "above", "unknown"];

// The hue for the entering strip, which is about grade placement ALONE.
//
// Deliberately not one of the three mastery colours. That chart's only variable
// is texture, so whatever hue carries it says nothing — and a hue that says
// nothing must not be one that says something everywhere else on the page. Ink
// grey is the honest choice: it reads as "these are just the bars", and white
// texture on it is legible at 3px, which a pale neutral would not be.
const NEUTRAL = PROSE;

// SVG pattern defs, one per (pattern x fill). Rendered inside each chart's own
// <svg> because pattern ids resolve per document, and a def in a sibling svg is
// a fill that silently comes out black.
//
// `scope` prefixes the ids so two charts on one page can't collide.
const PATTERN_FILLS = () => {
  const fills = STATES.map((k) => [k, stateOf(k).fill]);
  fills.push(["neutral", NEUTRAL]);
  return fills;
};

const patternId = (scope, pattern, fillKey) =>
  `${scope}-${pattern}-${fillKey}`;

// The stroke that draws the texture: the surface colour, not a darker tint of
// the fill. Cutting the fill away with the page's own white keeps every texture
// the same brightness relative to its background regardless of which hue it is
// riding on, which is what stops the amber bands reading as a lighter *value*
// of amber rather than as a texture.
const STROKE = { stroke: "#fff", strokeWidth: 1.6, strokeLinecap: "square",
                 opacity: 0.85 };

const GrowthDefs = ({ scope }) => (
  <defs>
    {PATTERN_FILLS().map(([key, color]) => (
      <React.Fragment key={key}>
        {/* Rising to the right. SVG's y grows downward, so the stroke that
            climbs is the one ENDING at a smaller y. */}
        <pattern
          id={patternId(scope, "up", key)}
          width="6"
          height="6"
          patternUnits="userSpaceOnUse"
        >
          <rect width="6" height="6" fill={color} />
          <path d="M0,6 l6,-6 M-1.5,1.5 l3,-3 M4.5,7.5 l3,-3" {...STROKE} />
        </pattern>
        {/* Falling to the right. */}
        <pattern
          id={patternId(scope, "down", key)}
          width="6"
          height="6"
          patternUnits="userSpaceOnUse"
        >
          <rect width="6" height="6" fill={color} />
          <path d="M0,0 l6,6 M-1.5,4.5 l3,3 M4.5,-1.5 l3,3" {...STROKE} />
        </pattern>
        <pattern
          id={patternId(scope, "dots", key)}
          width="6"
          height="6"
          patternUnits="userSpaceOnUse"
        >
          <rect width="6" height="6" fill={color} />
          <circle cx="3" cy="3" r="1.35" fill="#fff" opacity="0.9" />
        </pattern>
      </React.Fragment>
    ))}
  </defs>
);

// What to paint a band of (state, relative placement) with. `shade` off is the
// grade channel turned off, and then every band is its flat mastery colour —
// which is the honest picture of "you asked not to see grade", rather than a
// texture left on with nothing to mean.
const fillFor = (scope, fillKey, rel, shade) => {
  const color = fillKey === "neutral" ? NEUTRAL : stateOf(fillKey).fill;
  const pattern = REL[rel] ? REL[rel].pattern : "solid";
  if (!shade || pattern === "solid") return color;
  return `url(#${patternId(scope, pattern, fillKey)})`;
};

// The Legend's swatch can't reference the SVG defs, so the same four textures
// exist a second time as CSS. Kept beside their SVG twins rather than in the
// component that uses them, because the day one of them changes is the day both
// have to.
// NOTE the sign. A CSS gradient's angle names the direction the colour TRAVELS,
// and the stripes lie perpendicular to it — so `45deg` (travelling up-and-right)
// draws stripes that fall to the right, the opposite of an SVG path drawn at the
// same nominal angle. Getting this backwards is what put a rising hatch in the
// key beside a falling one in the plot; keeping the keys semantic (`up`/`down`)
// is what makes the mismatch impossible to reintroduce silently.
const SWATCH_CSS = {
  up:
    "repeating-linear-gradient(-45deg, rgba(255,255,255,0.85) 0 1.6px, " +
    "transparent 1.6px 4.2px)",
  down:
    "repeating-linear-gradient(45deg, rgba(255,255,255,0.85) 0 1.6px, " +
    "transparent 1.6px 4.2px)",
  // One centred dot rather than a tiled field of them: `Swatch` sets
  // `background-image` alone, and a tile size can only be given through the
  // `background` shorthand — written there it is not a valid image value at all
  // and the swatch silently comes back solid, which is how "unknown" spent its
  // first render looking exactly like "on grade".
  dots:
    "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.92) 2px, " +
    "transparent 2.1px)",
  solid: null,
};

const relSwatch = (rel, color) => ({
  color,
  pattern: SWATCH_CSS[REL[rel].pattern],
  label: REL[rel].label,
});

// The mastery key. `shade` is passed so the note can say what the textures are
// doing when they're on, and stay quiet when they're not.
const stateLegend = () =>
  STATES.map((k) => ({
    color: stateOf(k).fill,
    label: stateOf(k).label,
    title: stateOf(k).blurb,
  }));

const relLegend = (color = NEUTRAL) => REL_ORDER.map((r) => relSwatch(r, color));

export {
  STATES,
  stateOf,
  REL,
  REL_ORDER,
  NEUTRAL,
  GrowthDefs,
  fillFor,
  patternId,
  stateLegend,
  relLegend,
  relSwatch,
};
